import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import toast from 'react-hot-toast';
import axios from 'axios';
import Modal from '../AccountModal';
import api from '../../services/api';
import { RootState } from '../../store';
import { logout, updateProfileProps } from '../../store/Auth.store';
import Loader from '../Loader';
import {
  Card, CardTitle, TInput, Input, InputLabel, ButtonContainer, SaveButton, SaveButtonTitle,
  RemoveAccountButton, RemoveContainer, MessageLabel, SectionDivider, SectionTitle, AddressGrid,
  AddressFieldFullWidth, Select,
} from './style';
import { requestSubscription } from '../../store/Subscription.store';

type Country = {
  nome_pais: string;
  nome_pais_int: string;
  sigla: string;
};

type BrazilianState = {
  sigla: string;
  nome: string;
  cidades: string[];
};

export default function AccountData() {
  const profile = useSelector((state: RootState) => state.auth.user);
  const [newPassword, setNewPassword] = useState('');
  const [phone, setPhone] = useState('');
  const [newPasswordConfirmation, setNewPasswordConfirmation] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingPhone, setLoadingPhone] = useState(false);
  const [loadingAddress, setLoadingAddress] = useState(false);
  const [isConfirmationModal, setIsConfirmationModal] = useState(false);
  const [isSubscriptionConfirmationModal, setIsSubscriptionConfirmationModal] = useState(false);
  const [loadingCep, setLoadingCep] = useState(false);
  const [isCepTouched, setIsCepTouched] = useState(false);

  const [address, setAddress] = useState({
    zipcode: '',
    street: '',
    number: '',
    district: '',
    city: '',
    state: '',
    complement: '',
    country: '',
  });

  const [countries, setCountries] = useState<Country[]>([]);
  const [brazilianStates, setBrazilianStates] = useState<BrazilianState[]>([]);
  const [cities, setCities] = useState<string[]>([]);

  const isBrazil = address.country === 'Brasil';

  const dispatch = useDispatch();
  const subscription = useSelector(
    (state: RootState) => state.subscription.subscription,
  );

  useEffect(() => {
    setPhone(profile.phone ? profile.phone : '');

    if (profile.address) {
      setAddress({
        zipcode: profile.address.zipcode || '',
        street: profile.address.street || '',
        number: profile.address.number || '',
        district: profile.address.district || '',
        city: profile.address.city || '',
        state: profile.address.state || '',
        complement: profile.address.complement || '',
        country: profile.address.country || '',
      });
    }
  }, []);

  useEffect(() => {
    dispatch(requestSubscription() as any);
  }, []);

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const response = await api.get('/countries');
        setCountries(response.data);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Erro ao buscar países:', error);
      }
    };
    fetchCountries();
  }, []);

  useEffect(() => {
    if (isBrazil) {
      const fetchStates = async () => {
        try {
          const response = await api.get('/states');
          setBrazilianStates(response.data.estados);
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Erro ao buscar estados:', error);
        }
      };
      fetchStates();
    }
  }, [isBrazil]);

  useEffect(() => {
    if (isBrazil && address.state) {
      const selectedState = brazilianStates.find(
        (s) => s.sigla === address.state || s.nome === address.state,
      );
      setCities(selectedState ? selectedState.cidades : []);
    } else {
      setCities([]);
    }
  }, [address.state, brazilianStates, isBrazil]);

  useEffect(() => {
    const fetchAddressByCep = async (cep: string) => {
      try {
        setLoadingCep(true);
        const response = await axios.get(`https://viacep.com.br/ws/${cep}/json`);
        const { erro, ...data } = response.data;

        if (erro) {
          setLoadingCep(false);
          return;
        }

        setAddress((prev) => ({
          ...prev,
          street: data.logradouro || prev.street,
          district: data.bairro || prev.district,
          city: data.localidade || prev.city,
          state: data.uf || prev.state,
          complement: data.complemento || prev.complement,
          country: 'Brasil',
        }));

        setLoadingCep(false);
      } catch (error) {
        setLoadingCep(false);
        // eslint-disable-next-line no-console
        console.error('Erro ao buscar CEP:', error);
      }
    };

    const cleanCep = address.zipcode.replace(/\D/g, '');
    if (cleanCep.length === 8 && isCepTouched) {
      fetchAddressByCep(cleanCep);
    }
  }, [address.zipcode, isCepTouched]);

  const dispatchLogout = () => {
    dispatch(logout());
  };

  const clearPassword = () => {
    setNewPassword('');
    setNewPasswordConfirmation('');
  };

  const toggleConfirmationModal = () => {
    setIsConfirmationModal((isOpen) => !isOpen);
  };

  const toggleSubscriptionConfirmationModal = () => {
    setIsSubscriptionConfirmationModal((isOpen) => !isOpen);
  };

  const handleUpdateData = async () => {
    try {
      if (!newPassword && !newPasswordConfirmation) {
        toast.error('Preencha todos os campos!');
        return;
      }

      if (newPassword !== newPasswordConfirmation) {
        toast.error('As senhas não conferem, corrija!');
        return;
      }

      const payload = {
        password: newPassword,
        password_confirmation: newPasswordConfirmation,
      };

      setLoading(true);

      await api.put(`/users/${profile.id}`, payload);

      setLoading(false);

      clearPassword();

      toast('Senha alterada com sucesso!');
    } catch (error) {
      setLoading(false);

      toast.error('Ocorreu um erro ao alterar sua senha, tente novamente!');
    }
  };

  const handleUpdatePhone = async () => {
    try {
      if (!phone) {
        toast.error('Preencha todos os campos!');
        return;
      }

      const payload = {
        phone,
      };

      setLoadingPhone(true);

      await api.put(`/users/${profile.id}`, payload);

      dispatch(updateProfileProps(payload) as any);

      setLoadingPhone(false);

      toast('Telefone atualizado com sucesso!');
    } catch (error) {
      setLoadingPhone(false);

      toast.error('Ocorreu um erro ao atualizar seu telefone, tente novamente!');
    }
  };

  const handleAddressChange = (field: string, value: string) => {
    setAddress((prev) => ({ ...prev, [field]: value }));
  };

  const handleCountryChange = (value: string) => {
    setAddress((prev) => ({
      ...prev,
      country: value,
      state: '',
      city: '',
    }));
    setCities([]);
  };

  const handleStateChange = (value: string) => {
    setAddress((prev) => ({
      ...prev,
      state: value,
      city: '',
    }));
  };

  const handleUpdateAddress = async () => {
    try {
      if (!address.zipcode || !address.street || !address.number || !address.district
        || !address.city || !address.state || !address.country) {
        toast.error('Preencha todos os campos obrigatórios do endereço!');
        return;
      }

      const payload = {
        address: {
          street: address.street,
          number: address.number,
          district: address.district,
          city: address.city,
          state: address.state,
          complement: address.complement,
          country: address.country,
          zipcode: address.zipcode,
        },
      };

      setLoadingAddress(true);

      await api.put(`/users/${profile.id}`, payload);

      dispatch(updateProfileProps(payload) as any);

      setLoadingAddress(false);

      toast('Endereço atualizado com sucesso!');
    } catch (error) {
      setLoadingAddress(false);

      toast.error('Ocorreu um erro ao atualizar seu endereço, tente novamente!');
    }
  };

  const removeAccount = async () => {
    await api.delete(`/delete-user?userId=${profile.id}`)
      .then(() => {
        toast(
          'Sua conta foi removida com sucesso!',
        );
        dispatchLogout();
      })
      .catch(() => {
        toast.error('Não foi possível excluir a conta, tente novamente.');
      });
  };

  return (
    <>
      <Modal
        isOpen={isConfirmationModal}
        toggle={toggleConfirmationModal}
        onClick={removeAccount}
        title="Excluir conta"
        info="Deseja mesmo excluir sua conta?"
        confirmText="Excluir"
      />
      <Modal
        isOpen={isSubscriptionConfirmationModal}
        toggle={toggleSubscriptionConfirmationModal}
        onClick={removeAccount}
        title="Você ainda possui período de adesão pago em sua assinatura vigente"
        info="Ao excluir sua conta, você perderá esse período."
        confirmText="Excluir"
      />

      <Card data-testid="account-container">
        <CardTitle>DADOS DA CONTA</CardTitle>

        <InputLabel>Email</InputLabel>
        <Input
          value={profile.email}
          readOnly
        />
        <MessageLabel>
          *Para alterar o email de sua conta, faça contato com nosso time de assinaturas, no menu ajuda.
        </MessageLabel>
        <br />
        <br />

        <InputLabel>Nova senha</InputLabel>
        <Input
          type="password"
          placeholder="Sua nova senha"
          value={newPassword}
          onChange={({ currentTarget: { value } }) => setNewPassword(value)}
        />

        <InputLabel>Confirmação da nova senha</InputLabel>
        <Input
          type="password"
          placeholder="Confirme sua nova senha"
          value={newPasswordConfirmation}
          onChange={
            ({ currentTarget: { value } }) => setNewPasswordConfirmation(value)

          }
        />

        <ButtonContainer>
          <SaveButton onClick={handleUpdateData}>
            <SaveButtonTitle>Salvar</SaveButtonTitle>
            {loading && <Loader />}
          </SaveButton>
        </ButtonContainer>

        <SectionDivider />

        <InputLabel>Telefone</InputLabel>
        <TInput
          mask={phone.length > 10 ? phone.length > 11 ? '+99 (99) 9.9999-9999' : '(99) 9.9999-99999' : '(99) 9999-99999'}
          onChange={(e) => {
            setPhone(e.target.value.replace(/\D/g, ''));
          }}
          value={phone}
          type="tel"
          placeholder="(00) 0.0000-0000"
          disabled={loading}
          maskChar={null}
        />
        <ButtonContainer>
          <SaveButton onClick={handleUpdatePhone}>
            <SaveButtonTitle>Atualizar Telefone</SaveButtonTitle>
            {loadingPhone && <Loader />}
          </SaveButton>
        </ButtonContainer>

        <SectionDivider />

        <SectionTitle>Endereço</SectionTitle>

        <AddressGrid>
          <div>
            <InputLabel>CEP</InputLabel>
            <TInput
              mask="99999-999"
              value={address.zipcode}
              onChange={(e) => {
                setIsCepTouched(true);
                handleAddressChange('zipcode', e.target.value.replace(/\D/g, ''));
              }}
              placeholder="00000-000"
              maskChar={null}
            />
            {loadingCep && <span style={{ color: '#aaa', fontSize: 11 }}>Buscando endereço...</span>}
          </div>

          <div>
            <InputLabel>País</InputLabel>
            <Select
              value={address.country}
              onChange={(e) => handleCountryChange(e.target.value)}
            >
              <option value="">Selecione o país</option>
              {countries.map((country) => (
                <option key={country.sigla} value={country.nome_pais}>
                  {country.nome_pais}
                </option>
              ))}
            </Select>
          </div>

          <AddressFieldFullWidth>
            <InputLabel>Rua</InputLabel>
            <Input
              value={address.street}
              onChange={(e) => handleAddressChange('street', e.currentTarget.value)}
              placeholder="Nome da rua"
            />
          </AddressFieldFullWidth>

          <div>
            <InputLabel>Número</InputLabel>
            <Input
              value={address.number}
              onChange={(e) => handleAddressChange('number', e.currentTarget.value)}
              placeholder="Número"
            />
          </div>

          <div>
            <InputLabel>Complemento</InputLabel>
            <Input
              value={address.complement}
              onChange={(e) => handleAddressChange('complement', e.currentTarget.value)}
              placeholder="Apto, bloco, etc."
            />
          </div>

          <div>
            <InputLabel>Bairro</InputLabel>
            <Input
              value={address.district}
              onChange={(e) => handleAddressChange('district', e.currentTarget.value)}
              placeholder="Bairro"
            />
          </div>

          <div>
            <InputLabel>Estado</InputLabel>
            {isBrazil ? (
              <Select
                value={address.state}
                onChange={(e) => handleStateChange(e.target.value)}
              >
                <option value="">Selecione o estado</option>
                {brazilianStates.map((state) => (
                  <option key={state.sigla} value={state.sigla}>
                    {state.nome}
                  </option>
                ))}
              </Select>
            ) : (
              <Input
                value={address.state}
                onChange={(e) => handleAddressChange('state', e.currentTarget.value)}
                placeholder="Estado"
              />
            )}
          </div>

          <div>
            <InputLabel>Cidade</InputLabel>
            {isBrazil ? (
              <Select
                value={address.city}
                onChange={(e) => handleAddressChange('city', e.target.value)}
              >
                <option value="">Selecione a cidade</option>
                {cities.map((city) => (
                  <option key={city} value={city}>
                    {city}
                  </option>
                ))}
              </Select>
            ) : (
              <Input
                value={address.city}
                onChange={(e) => handleAddressChange('city', e.currentTarget.value)}
                placeholder="Cidade"
              />
            )}
          </div>
        </AddressGrid>

        <ButtonContainer>
          <SaveButton onClick={handleUpdateAddress}>
            <SaveButtonTitle>Atualizar Endereço</SaveButtonTitle>
            {loadingAddress && <Loader />}
          </SaveButton>
        </ButtonContainer>
      </Card>
      <RemoveContainer>
        <RemoveAccountButton onClick={subscription?.status === 'active'
          ? toggleSubscriptionConfirmationModal
          : toggleConfirmationModal}
        >
          Excluir Conta
        </RemoveAccountButton>
      </RemoveContainer>
    </>
  );
}
